// User profile (role, tenantId) stored in Realtime Database
import { ref, get, set, onValue } from 'firebase/database';
import { db } from './config';
import { getTenantById } from './tenants';

const USERS_ROOT = 'users';

export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  TENANT_ADMIN: 'tenant_admin'
};

/**
 * Get user profile by uid (role, tenantId, email).
 * @param {string} uid - Firebase Auth uid
 * @returns {Promise<{ success: boolean, profile?: { uid, email, role, tenantId } }>}
 */
export const getUserProfile = async (uid) => {
  try {
    const userRef = ref(db, `${USERS_ROOT}/${uid}`);
    const snapshot = await get(userRef);
    if (snapshot.exists()) {
      return {
        success: true,
        profile: { uid, ...snapshot.val() }
      };
    }
    return { success: false, message: 'User profile not found' };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: error.message || 'Failed to load user profile'
    };
  }
};

/**
 * Subscribe to user profile changes.
 * @param {string} uid
 * @param {(profile|null)} callback - called with profile object or null
 * @returns {() => void} unsubscribe
 */
export const listenUserProfile = (uid, callback) => {
  const userRef = ref(db, `${USERS_ROOT}/${uid}`);
  return onValue(userRef, (snapshot) => {
    if (snapshot.exists()) {
      callback({ uid, ...snapshot.val() });
    } else {
      callback(null);
    }
  });
};

/**
 * Set or create user profile (for super admin: create tenant admins; or first-time setup).
 * @param {string} uid - Firebase Auth uid
 * @param {{ email: string, role: string, tenantId?: string }} data
 */
export const setUserProfile = async (uid, data) => {
  try {
    const userRef = ref(db, `${USERS_ROOT}/${uid}`);
    await set(userRef, {
      email: data.email || '',
      role: data.role || ROLES.TENANT_ADMIN,
      tenantId: data.tenantId ?? null
    });
    return { success: true, message: 'Profile saved' };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: error.message || 'Failed to save profile'
    };
  }
};

/**
 * Resolve full auth context: profile + tenant snapshot for tenant_admin.
 * @param {string} uid
 * @param {string} email - from auth.user.email
 * @returns {Promise<{ role, tenantId, tenant|null }>}
 */
export const resolveAuthContext = async (uid, email) => {
  const profileResult = await getUserProfile(uid);
  const profile = profileResult.success ? profileResult.profile : null;

  if (!profile) {
    return { role: null, tenantId: null, tenant: null };
  }

  const role = profile.role || ROLES.TENANT_ADMIN;
  const tenantId = profile.tenantId || null;

  let tenant = null;
  if (tenantId) {
    const tenantResult = await getTenantById(tenantId);
    if (tenantResult.success) {
      tenant = tenantResult.tenant;
    }
  }

  return {
    role,
    tenantId,
    tenant,
    email: profile.email || email
  };
};
